import React, { useState, useEffect, useRef } from 'react'
import { Bell, X, Check, Trash2, FileText, DollarSign, CreditCard, AlertCircle } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

const NotificationBell = ({ userId }) => {
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [showDropdown, setShowDropdown] = useState(false)
  const [loading, setLoading] = useState(false)
  const dropdownRef = useRef(null)
  const navigate = useNavigate()

  // Load notifications
  const loadNotifications = async () => {
    if (!userId) return

    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('customer_id', userId)
        .neq('notification_status', 'DELETED')
        .order('created_at', { ascending: false })
        .limit(20)

      if (error) throw error

      setNotifications(data || [])
      setUnreadCount(data?.filter(n => !n.is_read).length || 0)
    } catch (error) {
      console.error('Error loading notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadNotifications()
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(loadNotifications, 30000)
    return () => clearInterval(interval)
  }, [userId])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const markAsRead = async (notificationId) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({
          is_read: true,
          read_at: new Date().toISOString(),
          notification_status: 'READ',
          updated_at: new Date().toISOString()
        })
        .eq('notification_id', notificationId)

      if (error) throw error

      // Update local state
      setNotifications(prev => {
        const updated = prev.map(n =>
          n.notification_id === notificationId
            ? { ...n, is_read: true, notification_status: 'READ' }
            : n
        )
        // Recalculate unread count
        const newUnreadCount = updated.filter(n => !n.is_read).length
        setUnreadCount(newUnreadCount)
        return updated
      })
    } catch (error) {
      console.error('Error marking as read:', error)
      toast.error('Failed to mark as read')
    }
  }

  const deleteNotification = async (notificationId) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({
          notification_status: 'DELETED',
          updated_at: new Date().toISOString()
        })
        .eq('notification_id', notificationId)

      if (error) throw error

      // Remove from local state
      setNotifications(prev => {
        const updated = prev.filter(n => n.notification_id !== notificationId)
        // Recalculate unread count
        const newUnreadCount = updated.filter(n => !n.is_read).length
        setUnreadCount(newUnreadCount)
        return updated
      })
      toast.success('Notification deleted')
    } catch (error) {
      console.error('Error deleting notification:', error)
      toast.error('Failed to delete notification')
    }
  }

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.is_read) {
      markAsRead(notification.notification_id)
    }

    // Navigate to action URL if available
    if (notification.action_url) {
      setShowDropdown(false)
      navigate(notification.action_url + `?user_id=${userId}`)
    }
  }

  const getNotificationIcon = (type) => {
    if (type?.includes('CLAIM')) return <FileText className="h-4 w-4" />
    if (type?.includes('PAYMENT')) return <DollarSign className="h-4 w-4" />
    if (type?.includes('POLICY')) return <CreditCard className="h-4 w-4" />
    return <AlertCircle className="h-4 w-4" />
  }

  const getNotificationColor = (priority) => {
    switch (priority) {
      case 'URGENT': return 'bg-red-100 text-red-600'
      case 'HIGH': return 'bg-orange-100 text-orange-600'
      default: return 'bg-blue-100 text-blue-600'
    }
  }

  const formatTime = (timestamp) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative text-gray-500 hover:text-gray-700 p-2"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <div className="fixed sm:absolute left-1/2 sm:left-auto right-auto sm:right-0 -translate-x-1/2 sm:translate-x-0 mt-2 w-[calc(100vw-2rem)] sm:w-96 max-w-sm sm:max-w-none bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[32rem] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            <span className="text-xs text-gray-500">{unreadCount} unread</span>
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-500 mt-2 text-sm">Loading...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.notification_id}
                  className={`p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                    !notification.is_read ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    {/* Icon */}
                    <div className={`p-2 rounded-full ${getNotificationColor(notification.notification_priority)}`}>
                      {getNotificationIcon(notification.notification_type)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div
                          className="flex-1 cursor-pointer"
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <h4 className="text-sm font-semibold text-gray-900 mb-1">
                            {notification.notification_title}
                          </h4>
                          <p className="text-xs text-gray-600 line-clamp-2">
                            {notification.notification_message}
                          </p>
                          <div className="flex items-center space-x-2 mt-2">
                            <span className="text-xs text-gray-500">
                              {formatTime(notification.created_at)}
                            </span>
                            {notification.is_actionable && (
                              <span className="text-xs text-blue-600 font-medium">
                                {notification.action_label}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center space-x-1 ml-2">
                          {!notification.is_read && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                markAsRead(notification.notification_id)
                              }}
                              className="p-1 hover:bg-green-100 rounded transition-colors"
                              title="Mark as read"
                            >
                              <Check className="h-3.5 w-3.5 text-green-600" />
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              deleteNotification(notification.notification_id)
                            }}
                            className="p-1 hover:bg-red-100 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5 text-red-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between sticky bottom-0">
              <button
                onClick={() => {
                  setShowDropdown(false)
                  navigate(`/customer/notifications?user_id=${userId}`)
                }}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                View All Notifications
              </button>
              <button
                onClick={() => setShowDropdown(false)}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Close
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default NotificationBell

